import { Link } from "@react-navigation/native";
import {
    Alert,
    DataTable,
    Headline,
    Loading,
    SearchInput,
    Spacer,
    StyleFunction,
    Surface,
    usePagination,
    useThemedStyle,
} from "@venuepos/react-common";
import { useCustomersInGroupLazyQuery } from "graphql-sdk";
import { ICustomerGroup } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect, useState } from "react";

export function GroupCustomers({ group }: { group: ICustomerGroup["id"] }) {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const { page, pageSize, sortBy, sortDirection, onPageChange } =
        usePagination({
            initialPageSize: 10,
            initialSortBy: "name",
            initialSortDirection: "ASC",
        });

    const [getCustomersInGroup, { error, loading, data }] =
        useCustomersInGroupLazyQuery();

    const [search, setSearch] = useState<string>("");
    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list
            onPageChange(0);
        },
        [onPageChange]
    );

    useEffect(() => {
        if (!group) {
            return;
        }

        getCustomersInGroup({
            variables: {
                groupId: group,
                pagination: {
                    page,
                    pageSize,
                    sort: sortBy,
                    sortDirection: sortDirection,
                },
                search: {
                    query: search,
                },
            },
        });
    }, [
        getCustomersInGroup,
        group,
        page,
        pageSize,
        search,
        sortBy,
        sortDirection,
    ]);

    return (
        <Surface>
            <Headline size="h5">
                {t("backoffice.customers.in_group", "Customers in this group")}
            </Headline>
            <SearchInput
                onChange={handleSearchTextChange}
                testID="customerSearch"
                onClear={() => setSearch("")}
            />
            <DataTable>
                <DataTable.Header>
                    <DataTable.Title>
                        {t("common.name", "Name")}
                    </DataTable.Title>
                </DataTable.Header>
                {error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: error.message,
                            }
                        )}
                    </Alert>
                ) : loading ? (
                    <>
                        <Spacer />
                        <Loading />
                        <Spacer />
                    </>
                ) : (
                    data?.customersInGroup.data.map((item, index) => (
                        <DataTable.Row
                            style={index % 2 === 0 ? styles.oddRow : undefined}
                            key={item.id}
                        >
                            <DataTable.Cell>
                                <Link
                                    to={{
                                        screen: "CUSTOMER_VIEW",
                                        params: { id: item.id },
                                    }}
                                    style={styles.textLink}
                                >
                                    {item.name}
                                </Link>
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))
                )}
                <DataTable.Pagination
                    onPageChange={onPageChange}
                    pageSize={pageSize}
                    page={page}
                    numberOfPages={data?.customersInGroup.pagination.pages}
                    itemCount={data?.customersInGroup.pagination.resultCount}
                    disablePageSizeSelector
                />
            </DataTable>
        </Surface>
    );
}

const styleFunc: StyleFunction = theme => ({
    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
    textLink: {
        ...theme.styles.link,
    },
});
